import React, { useState, useEffect, useRef, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import ErrorBoundary from './components/ErrorBoundary';
import { ErrorProvider } from './contexts/ErrorContext';
import { createConsumer } from '@rails/actioncable';
import theme, { setActiveTheme } from './config/theme';
import { PrimaryButton, HoverCard } from './components/HoverButton';
import { getGravatarUrl } from './utils/gravatar.ts';
import { Logo } from './components/Logo.tsx';
import LoadingScreen from './components/LoadingScreen.tsx';
import PageContainer from './components/PageContainer.tsx';

const Login = React.lazy(() => import('./components/Login'));
const GameList = React.lazy(() => import('./components/GameList'));
const Game = React.lazy(() => import('./components/Game'));
const UserProfile = React.lazy(() => import('./components/UserProfile'));
const Settings = React.lazy(() => import('./components/Settings'));
const Rules = React.lazy(() => import('./components/Rules'));

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    currentUser {
      id
      email
      username
      theme
      gamesCount
      winsCount
      lossesCount
    }
  }
`;

const GET_GAMES = gql`
  query GetGames($limit: Int, $offset: Int) {
    games(limit: $limit, offset: $offset) {
      id
      status
      state
      winner {
        id
        email
        username
      }
      players {
        id
        email
        username
      }
      playerOne {
        email
        username
      }
      playerTwo {
        email
        username
      }
    }
  }
`;

const CREATE_GAME = gql`
  mutation CreateGame {
    createGame {
      game {
        id
        status
        players {
          id
          email
        }
      }
      errors
    }
  }
`;

// Component for the game list page
function GameListPage({ currentUser, onLogout }) {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const { data: gamesData, loading: gamesLoading, refetch: refetchGames } = useQuery(GET_GAMES, {
    skip: !currentUser,
    pollInterval: 0
  });
  
  const [createGame] = useMutation(CREATE_GAME, {
    onCompleted: (data) => {
      if (data?.createGame?.game?.id) {
        navigate(`/game/${data.createGame.game.id}`);
      }
      refetchGames();
    }
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    const token = localStorage.getItem('authToken');
    if (!token) return;

    const wsUrl = process.env.REACT_APP_WS_URL || `/cable`;
    const cable = createConsumer(`${wsUrl}?token=${token}`);
    const subscription = cable.subscriptions.create(
      { channel: 'GameUpdatesChannel' },
      {
        received: (data) => {
          
          // Only process messages that affect the current user
          if (data.affected_users && data.affected_users.includes(currentUser.id)) {
            if (data.type === 'game_created' || data.type === 'game_updated' || data.type === 'game_deleted') {
              refetchGames();
            }
          }
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      cable.disconnect();
    };
  }, [currentUser, refetchGames]);

  const handleCreateGame = () => {
    createGame();
  };

  const handleSelectGame = (gameId) => {
    navigate(`/game/${gameId}`);
  };

  return (
    <PageContainer>
        <div className="flex justify-end items-center mb-8">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors hover:opacity-90 ${
                showDropdown ? 'bg-opacity-20' : ''
              }`}
              style={{
                backgroundColor: showDropdown ? `${theme.colors.text.secondary}20` : 'transparent'
              }}
            >
              <img
                src={getGravatarUrl(currentUser.email, 64)}
                alt={currentUser.username}
                className="w-8 h-8 rounded-full"
              />
              <span className="font-medium" style={{ color: theme.colors.text.secondary }}>{currentUser.username}</span>
              <svg 
                className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
                style={{ color: theme.colors.accent }} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showDropdown && (
              <div className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg py-1 z-10" style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border.light}` }}>
                <HoverCard
                  as="button"
                  onClick={() => {
                    setShowDropdown(false);
                    navigate(`/profile/${currentUser.id}`);
                  }}
                  className="w-full px-4 py-2 text-left flex items-center gap-2 transition-colors rounded"
                  style={{ color: theme.colors.text.primary }}
                  baseColor="transparent"
                  hoverColor={theme.colors.hover.light}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>Profile</span>
                </HoverCard>
                <HoverCard
                  as="button"
                  onClick={() => {
                    setShowDropdown(false);
                    navigate('/settings');
                  }}
                  className="w-full px-4 py-2 text-left flex items-center gap-2 transition-colors rounded"
                  style={{ color: theme.colors.text.primary }}
                  baseColor="transparent"
                  hoverColor={theme.colors.hover.light}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Settings</span>
                </HoverCard>
                <HoverCard
                  as="button"
                  onClick={() => {
                    setShowDropdown(false);
                    navigate('/rules');
                  }}
                  className="w-full px-4 py-2 text-left flex items-center gap-2 transition-colors rounded"
                  style={{ color: theme.colors.text.primary }}
                  baseColor="transparent"
                  hoverColor={theme.colors.hover.light}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <span>Rules</span>
                </HoverCard>
                <hr className="my-1" style={{ borderColor: theme.colors.border.light }} />
                <HoverCard
                  as="button"
                  onClick={() => {
                    onLogout();
                    setShowDropdown(false);
                  }}
                  className="w-full px-4 py-2 text-left flex items-center gap-2 transition-colors rounded"
                  style={{ color: theme.colors.status.error }}
                  baseColor="transparent"
                  hoverColor="rgba(239, 68, 68, 0.1)"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Log out
                </HoverCard>
              </div>
            )}
          </div>
        </div>

        {/* Logo */}
        <div className="flex justify-center mb-6">
          <Logo height={240} color={theme.colors.text.primary} />
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column - Actions */}
          <div className="lg:col-span-4 space-y-6">
            <div className="rounded-lg shadow-md p-6" style={{ backgroundColor: theme.colors.surface }}>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={getGravatarUrl(currentUser.email, 96)}
                  alt={currentUser.username}
                  className="w-12 h-12 rounded-full"
                />
                <div>
                  <div className="font-bold text-lg" style={{ color: theme.colors.text.primary }}>
                    {currentUser.username}
                  </div>
                  <button
                    onClick={() => navigate(`/profile/${currentUser.id}`)}
                    className="text-sm hover:underline"
                    style={{ color: theme.colors.text.link }}
                  >
                    View profile
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 pt-4" style={{ borderTop: `1px solid ${theme.colors.border.light}` }}>
                <div className="text-center">
                  <div className="text-2xl font-bold" style={{ color: theme.colors.text.primary }}>
                    {currentUser.gamesCount || 0}
                  </div>
                  <div className="text-xs" style={{ color: theme.colors.text.muted }}>
                    played
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold" style={{ color: theme.colors.status.success }}>
                    {currentUser.winsCount || 0}
                  </div>
                  <div className="text-xs" style={{ color: theme.colors.text.muted }}>
                    wins
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold" style={{ color: theme.colors.status.error }}>
                    {currentUser.lossesCount || 0}
                  </div>
                  <div className="text-xs" style={{ color: theme.colors.text.muted }}>
                    losses
                  </div>
                </div>
              </div>
            </div>

            <PrimaryButton
              onClick={handleCreateGame}
              gradient={`linear-gradient(to bottom, ${theme.colors.primary}, ${theme.colors.primaryHover})`}
              hoverGradient={`linear-gradient(to bottom, ${theme.colors.primaryHover}, ${theme.colors.primaryDark})`}
              className="w-full px-6 rounded-xl font-semibold text-xl tracking-wide transition-all duration-200 hover:shadow-lg flex items-center justify-center gap-3"
              style={{
                paddingTop: '3.375rem',
                paddingBottom: '3.375rem',
                color: theme.colors.text.button
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Create game
            </PrimaryButton>
          </div>

          {/* Right Column - Game List */}
          <div className="lg:col-span-8">
            <div className="rounded-lg shadow-md p-6" style={{ backgroundColor: theme.colors.surface }}>
              <GameList
                games={gamesData?.games || []}
                loading={gamesLoading}
                onSelectGame={handleSelectGame}
                currentUser={currentUser}
              />
            </div>
          </div>
        </div>
    </PageContainer>
  );
}

// Component for individual game page
function GamePage({ currentUser }) {
  const { gameId } = useParams();
  const navigate = useNavigate();
  
  const handleBackToGameList = () => {
    navigate('/');
  };

  return <Game gameId={gameId} onBack={handleBackToGameList} currentUser={currentUser} />;
}

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  
  const { data: userData, loading: userLoading } = useQuery(GET_CURRENT_USER, {
    skip: !localStorage.getItem('authToken')
  });

  useEffect(() => {
    if (userData?.currentUser) {
      setCurrentUser(userData.currentUser);
      // Set the active theme based on user preference
      setActiveTheme(userData.currentUser.theme || 'dark');
    }
  }, [userData]);

  useEffect(() => {
    const baseTitle = 'Cattle Line';
    document.title = process.env.NODE_ENV === 'development' ? `${baseTitle} (local)` : baseTitle;
  }, []);

  const handleLogin = (user, token) => {
    localStorage.setItem('authToken', token);
    setCurrentUser(user);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setCurrentUser(null);
  };

  if (userLoading) {
    return <LoadingScreen />;
  }

  if (!currentUser) {
    return (
      <ErrorProvider>
        <ErrorBoundary>
          <Router>
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
                <Route path="*" element={<Login onLogin={handleLogin} />} />
              </Routes>
            </Suspense>
          </Router>
        </ErrorBoundary>
      </ErrorProvider>
    );
  }

  return (
    <ErrorProvider>
      <ErrorBoundary>
        <Router>
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.colors.background }}>
              <div className="text-xl" style={{ color: theme.colors.text.secondary }}>Loading...</div>
            </div>
          }>
            <Routes>
              <Route path="/" element={<GameListPage currentUser={currentUser} onLogout={handleLogout} />} />
              <Route path="/game/:gameId" element={<GamePage currentUser={currentUser} />} />
              <Route path="/profile/:userId" element={<UserProfile />} />
              <Route path="/settings" element={<Settings currentUser={currentUser} />} />
              <Route path="/rules" element={<Rules />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Router>
      </ErrorBoundary>
    </ErrorProvider>
  );
}

export default App;
